import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, VideoLog, UserRole } from '../types';
import { api } from '../services/api';
import { StopCircle, LogOut, Video as VideoIcon, UploadCloud, Keyboard, Search, X, Loader2, AlertTriangle, FolderOpen, History } from 'lucide-react';
import { supabase } from '../lib/supabase'; // Import supabase directly for forced logout

// --- INDEXEDDB HELPERS (For Folder Memory) ---
const DB_NAME = 'PackerSettingsDB';
const STORE_NAME = 'settings';

const getDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (e: any) => {
      e.target.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = (e: any) => resolve(e.target.result);
    request.onerror = (e) => reject(e);
  });
};

const getDirectoryHandle = async (): Promise<FileSystemDirectoryHandle | null> => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get('videoSaveDir');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.error("DB Read Error", e);
    return null;
  }
};

const saveDirectoryHandle = async (handle: FileSystemDirectoryHandle) => {
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.put(handle, 'videoSaveDir');
};

// --- TYPES ---
interface PackerInterfaceProps {
  packer: UserProfile;
  onLogout: () => void;
}

interface QueueItem {
  id: string;
  blob: Blob;
  awb: string;
  filename: string;
  attempts: number;
  mimeType: string; // Track mimeType per file
}

const PackerInterface: React.FC<PackerInterfaceProps> = ({ packer, onLogout }) => {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  
  // Camera & Recording State
  const [recording, setRecording] = useState(false);
  const [awb, setAwb] = useState(''); 
  const [manualAwb, setManualAwb] = useState(''); 
  const [isScanning, setIsScanning] = useState(false); 
  const [showMobileInput, setShowMobileInput] = useState(false);
  const [scanBuffer, setScanBuffer] = useState(''); 

  // Queue State
  const [uploadQueue, setUploadQueue] = useState<QueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Logs & History
  const [logs, setLogs] = useState<VideoLog[]>([]);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const awbRef = useRef(''); 
  const scanTimerRef = useRef<any>(null);
  
  // Track key timing & active MimeType
  const lastKeyTimeRef = useRef<number>(0);
  const lastScanTimeRef = useRef<number>(0); 
  const activeMimeTypeRef = useRef<string>('video/webm');

  // --- 1. SETUP & HARDWARE ---
  useEffect(() => {
    const handleResize = () => setDevice(window.innerWidth < 768 ? 'mobile' : 'desktop');
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Scanner Input Handler
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.target instanceof HTMLInputElement) return;
        if (device === 'desktop') {
            const now = Date.now();

            if (e.key === 'Enter') {
                if (scanBuffer.length > 3) {
                    handleScan(scanBuffer);
                    setScanBuffer('');
                }
            } else if (e.key.length === 1) {
                // Smart Buffer Reset (Clears "c" or prefix chars if typed too slow)
                if (now - lastKeyTimeRef.current > 100) {
                     setScanBuffer(e.key);
                } else {
                     setScanBuffer(prev => prev + e.key);
                }
                lastKeyTimeRef.current = now;
            }
        }
    };
    window.addEventListener('keydown', handleKeyDown);

    // Initial Camera Start
    startCameraStream();
    
    // Initial Logs Fetch
    api.getLogs(packer.id, UserRole.PACKER).then(data => setLogs(data.slice(0,5))).catch(console.error);

    return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('keydown', handleKeyDown);
        stopCameraStream(); 
    };
  }, [scanBuffer, device, packer.id]);


  // --- 2. CAMERA MANAGEMENT ---
  const startCameraStream = async () => {
    try {
        if (videoRef.current && videoRef.current.srcObject) {
             const currentStream = videoRef.current.srcObject as MediaStream;
             if(currentStream.active && currentStream.getVideoTracks()[0].readyState === 'live') {
                 return;
             }
        }

        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
              facingMode: 'environment',
              width: { ideal: 1280 }, // HD Resolution usually helps with focus
              height: { ideal: 720 }
          }, 
          audio: false 
        });
        
        if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(e => console.log("Play error", e));
        }
    } catch (err) { console.error("Camera error:", err); }
  };

  const stopCameraStream = () => {
    if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
    }
  };


  // --- 3. RECORDING LOGIC ---
  const startRecording = async () => {
    if (!videoRef.current || !videoRef.current.srcObject) {
        console.log("Camera was cold, starting now...");
        await startCameraStream();
    }
    
    const stream = videoRef.current?.srcObject as MediaStream;
    if (!stream || !stream.active) {
        console.error("Camera failed to start, cannot record.");
        return;
    }

    const sessionAwb = awbRef.current; 

    // Smart Codec Selection
    const candidates = [
        'video/mp4',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp9',
        'video/webm'
    ];

    let selectedMime = 'video/webm';
    for (const type of candidates) {
        if (MediaRecorder.isTypeSupported(type)) {
            selectedMime = type;
            break;
        }
    }
    activeMimeTypeRef.current = selectedMime;
    console.log(`🎥 Using Format: ${selectedMime}`);

    try {
        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: selectedMime,
            videoBitsPerSecond: 2500000 // 2.5 Mbps
        });
        
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = () => {
            // Create Blob with the CORRECT mime type we selected
            const blob = new Blob(chunksRef.current, { type: activeMimeTypeRef.current });
            addToQueue(blob, sessionAwb, activeMimeTypeRef.current); 
        };

        mediaRecorder.start();
        setRecording(true);
    } catch (e) {
        console.error("MediaRecorder failed to start", e);
        alert("Camera Error: Please refresh the page.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
    }
    
    setRecording(false);
    setAwb('');
    awbRef.current = '';
    // Don't stop camera stream on desktop to keep it ready for next scan
    if (device === 'mobile') stopCameraStream(); 
  };

  const handleScan = async (scannedCode: string) => {
    if (!scannedCode) return;

    // 1 Second Debounce
    const now = Date.now();
    if (now - lastScanTimeRef.current < 1000 && scannedCode === awbRef.current) {
        return;
    }
    lastScanTimeRef.current = now;

    let cleanCode = scannedCode.trim();
    // Dedup check
    if (cleanCode.length > 8 && cleanCode.length % 2 === 0) {
        const half = cleanCode.length / 2;
        if (cleanCode.slice(0, half) === cleanCode.slice(half)) {
            cleanCode = cleanCode.slice(0, half);
        }
    }

    if (!recording) {
        setAwb(cleanCode);
        awbRef.current = cleanCode;
        setManualAwb('');
        setShowMobileInput(false);
        await startRecording();
    } else {
        if (cleanCode === awbRef.current) {
            stopRecording();
        } else {
            if(confirm(`Current AWB: ${awbRef.current}\nScanned: ${cleanCode}\n\nStop recording?`)) {
                 stopRecording();
            }
        }
    }
  };

  // --- 4. QUEUE & SAVING SYSTEM ---
  const addToQueue = (blob: Blob, recordedAwb: string, mimeType: string) => {
      const finalAwb = recordedAwb || `scan_${Date.now()}`;
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      
      const newItem: QueueItem = {
          id: Date.now().toString(),
          blob: blob,
          awb: finalAwb,
          filename: `${finalAwb}.${ext}`, 
          attempts: 0,
          mimeType: mimeType
      };
      
      setUploadQueue(prev => [...prev, newItem]);
      saveVideoToFolder(blob, newItem.filename);
  };

  const saveVideoToFolder = async (blob: Blob, filename: string) => {
    try {
        let dirHandle = await getDirectoryHandle();

        if (!dirHandle) {
            try {
                dirHandle = await window.showDirectoryPicker();
                await saveDirectoryHandle(dirHandle); 
            } catch (cancelErr) {
                downloadLocallyFallback(blob, filename);
                return;
            }
        }

        if (dirHandle) {
            const permission = await dirHandle.requestPermission({ mode: 'readwrite' });
            if (permission !== 'granted') {
                throw new Error("Permission not granted");
            }
            
            const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
        }

    } catch (err) {
        console.error("Advanced save failed, using fallback:", err);
        downloadLocallyFallback(blob, filename);
    }
  };

  const downloadLocallyFallback = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

// Queue Processor Effect (Robust Version)
  useEffect(() => {
      const processNext = async () => {
          // Stop if busy or empty
          if (isProcessing || uploadQueue.length === 0) return;

          setIsProcessing(true);
          const item = uploadQueue[0]; 

          try {
             await processUpload(item);
             
             // --- SUCCESS ---
             // 1. Remove from queue
             setUploadQueue(prev => prev.slice(1));
             
             // 2. Add Green "Completed" Log
             setLogs(prev => [{
                 id: item.id,
                 awb: item.awb,
                 packer_id: packer.id,
                 admin_id: packer.organization_id || '',
                 created_at: new Date().toISOString(),
                 video_url: '#',
                 status: 'completed',
                 whatsapp_status: 'pending'
             }, ...prev]);

          } catch (error: any) {
              console.error(`Upload error for ${item.awb}:`, error);

              // --- CRITICAL SECURITY CHECK ---
              // If token expired, force logout to prevent infinite loop of errors
              if (error.message.includes('401') || error.message.includes('403')) {
                  handleSafeLogout();
                  return;
              }

              // --- RETRY LOGIC ---
              if (item.attempts < 2) { 
                  // If tried less than 3 times (0, 1, 2), increment attempt count and keep in queue
                  console.log(`Retrying ${item.awb} (Attempt ${item.attempts + 1}/3)...`);
                  
                  setUploadQueue(prev => {
                      const updated = [...prev];
                      updated[0] = { ...updated[0], attempts: updated[0].attempts + 1 };
                      return updated;
                  });
                  
                  // Optional: Small delay before next retry prevents "hammering" the server
                  await new Promise(r => setTimeout(r, 2000)); 

              } else {
                  // --- PERMANENT FAILURE (After 3 tries) ---
                  // 1. Log as RED "Failed" so packer sees it in history
                  setLogs(prev => [{
                      id: item.id,
                      awb: item.awb,
                      packer_id: packer.id,
                      admin_id: packer.organization_id || '',
                      created_at: new Date().toISOString(),
                      video_url: '#',
                      status: 'failed', // <--- This will show yellow/red in your table
                      whatsapp_status: 'failed'
                  }, ...prev]);

                  // 2. Alert the user (Only on final failure)
                  alert(`❌ Final Failure: Could not upload ${item.awb} after 3 attempts.\nVideo is saved in your local folder.`);

                  // 3. NOW remove it from queue so we can move to the next item
                  setUploadQueue(prev => prev.slice(1));
              }

          } finally {
              setIsProcessing(false);
          }
      };

      processNext();
  }, [uploadQueue, isProcessing, packer.id, packer.organization_id]);

  // Upload Logic
  const processUpload = async (item: QueueItem) => {
        const tokenRes = await api.getUploadToken(item.filename, item.mimeType);
        const { accessToken, folderId } = tokenRes; 

        if (!accessToken) throw new Error("No access token received");

        const metadata = {
            name: item.filename,
            mimeType: item.mimeType,
            parents: folderId ? [folderId] : []
        };

        const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(metadata)
        });

        if (!createRes.ok) throw new Error(`Google Drive Metadata Failed: ${createRes.statusText}`);
        
        const fileData = await createRes.json();
        const realFileId = fileData.id;

        const uploadRes = await fetch(
            `https://www.googleapis.com/upload/drive/v3/files/${realFileId}?uploadType=media`,
            {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': item.mimeType
                },
                body: item.blob,
            }
        );

        if (!uploadRes.ok) throw new Error("Google Drive Content Upload Failed");

        const realVideoUrl = `https://drive.google.com/file/d/${realFileId}/view`;

        await api.completeFulfillment({
            awb: item.awb,
            videoUrl: realVideoUrl,
            folderId: folderId || ''
        });
  };

  // Safe Logout
  const handleSafeLogout = async () => {
      try {
          await supabase.auth.signOut(); 
      } catch (e) {
          console.warn("Server logout failed", e);
      } finally {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/'; 
      }
  };

  // --- UI HANDLERS ---
  const handleTouchStart = (e: React.TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'INPUT') return;
      e.preventDefault();
      if (recording) return;

      setIsScanning(true);
      scanTimerRef.current = setTimeout(() => {
          setIsScanning(false);
          const simulatedCode = `ASEN-${Math.floor(Math.random()*100000)}`;
          handleScan(simulatedCode);
          if (navigator.vibrate) navigator.vibrate(200); 
      }, 1500);
  };

  const handleTouchEnd = () => {
      if (recording) return;
      setIsScanning(false);
      if (scanTimerRef.current) { clearTimeout(scanTimerRef.current); scanTimerRef.current = null; }
  };

  const resetFolder = async () => {
      try {
          const handle = await window.showDirectoryPicker();
          await saveDirectoryHandle(handle);
          alert("Default folder updated successfully!");
      } catch (e) { }
  };

  // ------------------------------------------------------------------
  // UI RENDER
  // ------------------------------------------------------------------
  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      
      {/* HEADER */}
      <div className="bg-slate-900 px-4 py-2 flex justify-between items-center border-b border-slate-800 z-10 shrink-0 h-14">
        <div className="flex items-center gap-3">
            <div className="font-bold flex items-center gap-2 text-lg">
                <VideoIcon className="text-blue-500" />
                {device === 'mobile' ? 'Mobile Scanner' : 'Desktop Station'}
            </div>
            <span className="text-slate-600">|</span>
            <div className="text-xs text-slate-400">Packer: <span className="text-white">{packer.name}</span></div>
        </div>
        <div className="flex gap-4">
            <button onClick={resetFolder} className="text-slate-400 hover:text-white" title="Change Save Folder">
                <FolderOpen size={20} />
            </button>
            <button onClick={handleSafeLogout} className="text-slate-400 hover:text-red-400"><LogOut size={20} /></button>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* === LEFT SIDEBAR (Desktop Only) === */}
        {device === 'desktop' && (
            <div className="w-96 bg-slate-900 border-r border-slate-800 flex flex-col p-4 gap-6 shrink-0 z-20 shadow-xl">
                
                {/* 1. STATUS CARD */}
                <div className={`p-6 rounded-2xl flex flex-col items-center justify-center gap-2 border shadow-lg transition-all duration-300 ${
                    recording 
                    ? 'bg-red-900/20 border-red-500/50 shadow-red-900/20' 
                    : 'bg-slate-800 border-slate-700'
                }`}>
                    <div className={`w-4 h-4 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}></div>
                    <span className="font-mono text-2xl font-bold tracking-wider">
                        {recording ? 'RECORDING' : 'READY'}
                    </span>
                    {recording && (
                        <div className="font-mono text-blue-400 font-bold bg-black/30 px-3 py-1 rounded text-sm mt-1 border border-blue-500/30">
                            {awb}
                        </div>
                    )}
                </div>

                {/* 2. MANUAL INPUT */}
                <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Manual Entry</label>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Keyboard className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input 
                                type="text"
                                value={manualAwb}
                                onChange={(e) => setManualAwb(e.target.value)}
                                onKeyDown={(e) => { if(e.key === 'Enter' && manualAwb) handleScan(manualAwb); }}
                                disabled={recording}
                                placeholder="Scan or Type AWB"
                                className="w-full bg-black border border-slate-700 text-white pl-10 pr-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-600 transition-all"
                            />
                        </div>
                        {!recording ? (
                            <button 
                                onClick={() => manualAwb && handleScan(manualAwb)} disabled={!manualAwb}
                                className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800 disabled:text-slate-500 text-white px-4 rounded-lg font-bold text-sm transition-colors"
                            >
                                GO
                            </button>
                        ) : (
                            <button 
                                onClick={stopRecording}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 rounded-lg font-medium transition-colors flex items-center justify-center w-16"
                            >
                                <StopCircle size={20} />
                            </button>
                        )}
                    </div>
                </div>

                {/* 3. SESSION HISTORY */}
                <div className="flex-1 flex flex-col min-h-0 bg-black/20 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="p-3 bg-slate-800/50 border-b border-slate-700 flex items-center gap-2">
                        <History size={16} className="text-slate-400" />
                        <span className="text-xs font-bold text-slate-300 uppercase">Recent Scans</span>
                    </div>
                    <div className="overflow-y-auto flex-1 p-0">
                        <table className="w-full text-sm text-left">
                            <tbody className="divide-y divide-slate-800">
                                {logs.length === 0 && (
                                    <tr><td className="p-4 text-center text-slate-600 text-xs">No scans this session</td></tr>
                                )}
                                {logs.map(log => (
                                    <tr key={log.id} className="hover:bg-white/5 transition-colors">
                                        <td className="px-3 py-3">
                                            <div className="font-mono text-blue-400 font-bold text-xs">{log.awb}</div>
                                            <div className="text-[10px] text-slate-500">{new Date(log.created_at).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] border uppercase ${
                                                log.status === 'completed' 
                                                ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                                                : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                                            }`}>
                                                {log.status === 'completed' ? 'Done' : log.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {/* === CENTER VIDEO AREA === */}
        <div 
            className="flex-1 relative bg-black flex items-center justify-center overflow-hidden p-4"
            onTouchStart={device === 'mobile' ? handleTouchStart : undefined}
            onTouchEnd={device === 'mobile' ? handleTouchEnd : undefined}
        >
            {/* The Video Element - Object Contain ensures whole visual is seen */}
            <div className="relative w-full h-full max-w-[1920px] mx-auto flex items-center justify-center bg-gray-900 rounded-xl border border-slate-800 overflow-hidden shadow-2xl">
                <video 
                    ref={videoRef} 
                    autoPlay muted playsInline
                    className={`max-w-full max-h-full object-contain ${isScanning ? 'scale-105' : 'scale-100'} transition-transform duration-200`}
                />

                {/* Mobile Overlays (Only show on mobile or if sidebar is hidden) */}
                {device === 'mobile' && (
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}></div>
                        <span className="font-mono font-bold text-sm">
                            {recording ? `REC: ${awb}` : 'READY'}
                        </span>
                    </div>
                )}
            </div>
            
            {/* Visual Effects Overlay (Scanning Lines) */}
            {isScanning && (
                <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
                     <div className="w-full h-0.5 bg-red-500 shadow-[0_0_20px_rgba(239,68,68,1)] animate-pulse"></div>
                     <div className="absolute text-white font-mono font-bold text-lg bg-black/50 px-3 py-1 rounded mt-8 backdrop-blur-md">SCANNING...</div>
                </div>
            )}

            {/* Upload Queue Toast (Floating) */}
            {uploadQueue.length > 0 && (
                 <div className="absolute top-6 right-6 z-40 bg-blue-600 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-bounce">
                    <Loader2 className="animate-spin" size={20} />
                    <div className="flex flex-col leading-tight">
                        <span className="text-xs font-bold uppercase tracking-wider">Uploading</span>
                        <span className="text-xs text-blue-100">{uploadQueue.length} Pending...</span>
                    </div>
                 </div>
            )}

            {/* Mobile Specific Controls Overlay */}
            {device === 'mobile' && !recording && !isScanning && !showMobileInput && (
                <div className="absolute bottom-10 flex flex-col items-center gap-4 z-30 pointer-events-none">
                    <div className="text-white/70 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm text-sm animate-bounce">
                         Hold screen to scan
                    </div>
                    <button 
                        onClick={() => setShowMobileInput(true)}
                        className="pointer-events-auto bg-slate-800/80 p-3 rounded-full text-slate-300 hover:text-white backdrop-blur-md"
                    >
                        <Keyboard size={24} />
                    </button>
                </div>
            )}

             {/* Mobile Manual Input Overlay */}
             {device === 'mobile' && showMobileInput && !recording && (
                 <div className="absolute bottom-10 z-30 pointer-events-auto bg-black/80 p-4 rounded-xl w-64 backdrop-blur-md border border-slate-700">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-slate-300">Enter AWB</span>
                        <button onClick={() => setShowMobileInput(false)}><X size={16} /></button>
                    </div>
                    <input 
                        type="text" autoFocus value={manualAwb}
                        onChange={(e) => setManualAwb(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white mb-3 text-sm"
                        placeholder="ASEN..."
                    />
                    <button 
                        onClick={() => handleScan(manualAwb)} disabled={!manualAwb}
                        className="w-full bg-blue-600 py-2 rounded font-bold text-sm"
                    >
                        Start
                    </button>
                 </div>
            )}

            {/* Mobile Stop Button Overlay */}
            {device === 'mobile' && recording && (
                <div className="absolute bottom-10 z-30 pointer-events-auto">
                      <button 
                        onClick={(e) => { e.stopPropagation(); stopRecording(); }}
                        className="bg-red-600 text-white p-6 rounded-full shadow-lg shadow-red-900/50 flex flex-col items-center gap-1 active:scale-95"
                    >
                        <StopCircle size={32} />
                        <span className="text-[10px] font-bold">STOP</span>
                    </button>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

export default PackerInterface;
